import { useEffect, useMemo } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import classNames from "classnames/bind";
import Button from "common/blocks/Button";
import InfoButton from "common/blocks/InfoButton";
import TextInput from "common/blocks/TextInput";
import UIIcon from "common/blocks/UIIcon";
import useModalContext from "common/providers/modal/useModalContext";
import { valuationRangeModal, valuePartExchangeModal } from "features/vehicleDetails/modals";
import buildRichtextPopUp from "features/vehicleDetails/modals/RichTextPopup";
import usePartExchangeQuestionsContext from "features/vehicleDetails/providers/partExchangeQuestions/usePartExchangeQuestionsContext";
import { useForm } from "react-hook-form";
import { ResolvedHigherOfferSidePanel } from "resolvers/data/higherOfferSidePanelResolver";
import { ResolvedPopup } from "resolvers/data/popupResolver";
import { scrollIntoView } from "seamless-scroll-polyfill";
import { PartExchange } from "services/motorpoint/partExchange";
import formatCurrency from "utils/formatCurrency";
import validatePlateNumber from "utils/validatePlateNumber";

import styles from "./PartExchangeWidget.module.scss";
import { partExchangeWidgetSchema } from "./schema";

const cx = classNames.bind(styles);

export type HigherOfferContent = {
    higherOfferLabel: string;
    higherOfferButtonText: string;
    higherOfferSidePanel: ResolvedHigherOfferSidePanel;
};

export type PartExchangeWidgetProps = {
    strongText: string;
    text: string;
    buttonText: string;
    textInputPlaceholder: string;
    partExchangeLabel: string;
    vehicleLabel: string;
    equityLabel: string;
    valueLabel: string;
    altButtonText: string;
    removeButtonText: string;
    whatIsEquity: ResolvedPopup;
    higherOfferContent: HigherOfferContent;
};

export const WHAT_IS_EQUITY_POPUP_NAME = "__what-is-equity";

const PartExchangeWidget = ({
    strongText,
    text,
    buttonText,
    textInputPlaceholder,
    altButtonText,
    equityLabel,
    partExchangeLabel,
    removeButtonText,
    valueLabel,
    vehicleLabel,
    whatIsEquity,
    higherOfferContent,
}: PartExchangeWidgetProps) => {
    const { usePopup, useSidebar } = useModalContext();
    const { openSidebar } = useSidebar();
    const { registerPopups, openRegisteredPopup } = usePopup();

    const { formState, register, handleSubmit, setValue, watch } = useForm<{
        regEntry: string | undefined;
    }>({
        resolver: zodResolver(partExchangeWidgetSchema),
        defaultValues: { regEntry: undefined },
    });

    const { errors } = formState;

    const { partExQuestionsVehicle, partExQuestionsAnswersResponse, resetAllQuestionsData } =
        usePartExchangeQuestionsContext();

    const isServer = typeof document === "undefined"; // to avoid hydration error.
    const vrm =
        partExQuestionsVehicle?.VRM ??
        (partExQuestionsAnswersResponse as PartExchange)?.VehicleRegistration;
    const valuation =
        partExQuestionsAnswersResponse?.Valuation ?? partExQuestionsAnswersResponse?.Valuation;
    const valuationRange =
        partExQuestionsAnswersResponse?.ValuationRange ??
        partExQuestionsAnswersResponse?.ValuationRange;
    const equity = partExQuestionsAnswersResponse?.Equity ?? partExQuestionsAnswersResponse?.Equity;

    const formattedVRM = useMemo(() => validatePlateNumber(vrm || ""), [vrm]);

    useEffect(() => {
        registerPopups({ [WHAT_IS_EQUITY_POPUP_NAME]: buildRichtextPopUp(whatIsEquity) });
    }, [registerPopups, whatIsEquity]);

    const onSubmit = (data: any) =>
        openSidebar(valuePartExchangeModal, {
            registration_number: data.regEntry,
            higherOfferContent: higherOfferContent,
        });

    const onWhatIsEquityClick = () => openRegisteredPopup(WHAT_IS_EQUITY_POPUP_NAME);

    const openValuationRangeSidebar = () =>
        openSidebar(
            valuationRangeModal({
                higherOfferSidePanel: higherOfferContent.higherOfferSidePanel,
                partExchange: partExQuestionsAnswersResponse as PartExchange,
            })
        );

    if (formattedVRM && valuation && equity && (partExQuestionsAnswersResponse || isServer)) {
        return (
            <div id="part-exchange-widget" className={cx("partExWidget")}>
                {!valuationRange?.MaximumValuation && (
                    <div className={cx("iconRow")}>
                        <UIIcon icon="CurrencyExchange" />
                    </div>
                )}
                <div className={cx("wrapper")}>
                    <div className={cx("column")}>
                        <span className={cx("title")}>{partExchangeLabel}</span>
                        <div className={cx("details")}>
                            <span className={cx("label")}>{valueLabel}</span>
                            <div className={cx("valueWrapper")}>
                                <span
                                    className={cx("value")}
                                    dangerouslySetInnerHTML={{
                                        __html: formatCurrency(valuation, 2, true),
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    <div className={cx("column")}>
                        <span className={cx("vehicle")}>
                            {vehicleLabel}: <span>{formattedVRM.formatedPlate}</span>
                        </span>
                        <div className={cx("details")}>
                            <span className={cx("label")}>{equityLabel}</span>
                            <div className={cx("valueWrapper")}>
                                <span
                                    className={cx("value")}
                                    dangerouslySetInnerHTML={{
                                        __html: formatCurrency(equity, 2, true),
                                    }}
                                />
                                <InfoButton onClick={onWhatIsEquityClick} />
                            </div>
                        </div>
                    </div>
                </div>
                <div className={cx("actions")}>
                    <Button
                        theme="secondary"
                        className={cx("secondaryButton")}
                        onClick={() => {
                            const fc = document.getElementById("finance-calculator");

                            if (fc) {
                                scrollIntoView(fc, { block: "center", behavior: "smooth" });
                            }
                        }}
                        ariaLabel={altButtonText}
                    >
                        {altButtonText}
                    </Button>
                    <div className={cx("linkButtonWrapper")}>
                        <Button
                            theme="textLink"
                            onClick={() => {
                                resetAllQuestionsData();
                                setValue("regEntry", "");
                            }}
                            ariaLabel={removeButtonText}
                        >
                            {removeButtonText}
                        </Button>
                    </div>
                </div>
                {!!valuationRange?.MaximumValuation && (
                    <div className={cx("offerMore")}>
                        <span className={cx("label")}>{higherOfferContent.higherOfferLabel}</span>
                        <div className={cx("valueWrapper")}>
                            <span className={cx("upToLabel")}>up to</span>
                            <span
                                className={cx("upToValue")}
                                dangerouslySetInnerHTML={{
                                    __html: formatCurrency(
                                        valuationRange.MaximumValuation,
                                        2,
                                        true
                                    ),
                                }}
                            ></span>
                        </div>
                        <div className={cx("colourScale")} />
                        <div className={cx("linkButtonWrapper")}>
                            <Button
                                theme="textLink"
                                ariaLabel={higherOfferContent.higherOfferButtonText}
                                onClick={openValuationRangeSidebar}
                            >
                                {higherOfferContent.higherOfferButtonText}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div id="part-exchange-widget" className={cx("partExWidget", "form")}>
            <div className={cx("iconRow")}>
                <UIIcon icon="CurrencyExchange" />
            </div>
            {!!text && !!strongText && (
                <div className={cx("textRow")}>
                    {!!strongText && <span className={cx("textRowMain")}>{strongText}</span>}
                    {!!text && text}
                </div>
            )}
            <form className={cx("inputRow")} onSubmit={handleSubmit(onSubmit)}>
                <TextInput
                    placeholder={textInputPlaceholder}
                    maxLength={7}
                    className={cx("input")}
                    {...register("regEntry", {
                        onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                            const { value } = event.target;

                            setValue(
                                "regEntry",
                                value
                                    .replaceAll(" ", "")
                                    .replaceAll(/[\W_]+/g, "")
                                    .toUpperCase()
                            );
                        },
                    })}
                    value={watch("regEntry")}
                    hasError={!!errors?.regEntry?.message}
                    errorText={errors?.regEntry?.message}
                />
                {!!buttonText && (
                    <Button
                        className={cx("inputRowButton")}
                        type="submit"
                        theme="secondary"
                        ariaLabel={buttonText}
                    >
                        {buttonText}
                    </Button>
                )}
            </form>
        </div>
    );
};

export default PartExchangeWidget;
